'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import { lockBodyScroll } from '@/utils/scrollLock';

const publicApiKey = process.env.NEXT_PUBLIC_KLAVIYO_PUBLIC_API_KEY;

// Klaviyo adds this class to <body> while one of its popups is open and removes
// it on close. Its own rule is `overflow: hidden`, which turns <body> into a
// scroll container and drops the sticky header out of view (BL-798, the same
// bug the Zaius modal had). globals.css neutralises that rule; this swaps in
// the position:fixed lock the rest of the site uses, for as long as the class
// is there. Keyed off the class rather than our own openForm call, so the
// teaser and any form Klaviyo opens by itself are covered too.
const PREVENT_SCROLL_CLASS = 'klaviyo-prevent-body-scrolling';

const useKlaviyoScrollLock = () => {
  useEffect(() => {
    const body = document.body;
    let unlock = null;

    const sync = () => {
      const open = body.classList.contains(PREVENT_SCROLL_CLASS);
      if (open && !unlock) unlock = lockBodyScroll();
      if (!open && unlock) {
        unlock();
        unlock = null;
      }
    };

    const observer = new MutationObserver(sync);
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    sync();

    return () => {
      observer.disconnect();
      unlock?.();
    };
  }, []);
};

const KlaviyoScript = () => {
  useKlaviyoScrollLock();

  if (!publicApiKey) return null;

  return (
    <Script
      id="klaviyo-init"
      strategy="afterInteractive"
      src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${publicApiKey}`}
    />
  );
};

export default KlaviyoScript;
